import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from './backend.service';
import { environment } from 'src/environments/environment';
import { BaseResponse } from 'src/shared/models/base-response.model';
import { UserResponse, User } from '../../shared/models/user.model';
import {
	Appointment,
	AppointmentResponse,
} from 'src/shared/models/appointment.model';
import { Insurance } from 'src/shared/models/insurance.model';
import { Dependent } from 'src/shared/models/dependent.model';
import { EmergencyInfo } from 'src/shared/models/emergency-info.model';
import { UserBasics } from 'src/shared/models/user-basics.model';
import { UserQatarInfo } from 'src/shared/models/user-qatar-info.model';

const API_URL = `${environment.apiUrl}/web/hr`;

export const ARCHIVE_USER_URL = `${API_URL}/archiveuser`;
export const ARCHIVE_USER_RESPONSE = 'Archived User';

export const ACTIVATE_USER_URL = `${API_URL}/activateuser`;
export const ACTIVATE_USER_RESPONSE = 'Activated User';

export const SAVE_USER_BASICS_URL = `${API_URL}/saveuserbasics`;
export const SAVE_USER_BASICS_RESPONSE = 'Saved User Basics';

export const SAVE_USER_APPOINTMENTS_URL = `${API_URL}/saveuserappointments`;
export const SAVE_USER_APPOINTMENTS_RESPONSE = 'Saved User Appointments';

export const SAVE_USER_QATAR_INFO_URL = `${API_URL}/saveuserqatarinfo`;
export const SAVE_USER_QATAR_INFO_RESPONSE = 'Saved User Qatar Info';

export const SAVE_USER_INSURANCE_URL = `${API_URL}/saveuserinsurance`;
export const SAVE_USER_INSURANCE_RESPONSE = 'Saved User Insurance';

export const SAVE_USER_EMERGENCY_INFO_URL = `${API_URL}/saveuseremergencyinfo`;
export const SAVE_USER_EMERGENCY_INFO_RESPONSE = 'Saved User Emergency Info';

export const SAVE_USER_DEPENDENTS_URL = `${API_URL}/saveuserdependents`;
export const SAVE_USER_DEPENDENTS_RESPONSE = 'Saved User Dependents';

@Injectable({
	providedIn: 'root',
})
export class HrBackendService {
	private backend = inject(BackendService);

	/**
	 * Archive User
	 * =============
	 * API Endpoint: /web/hr/archiveuser
	 * JSON Parameters: id
	 *
	 * Archives the user and returns a the updated User object
	 */
	archiveUser(userId: number): Observable<User> {
		return this.backend
			.post(ARCHIVE_USER_URL, ARCHIVE_USER_RESPONSE, {
				id: userId,
			})
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Activate User
	 * ==============
	 * API Endpoint: /web/hr/activateuser
	 * JSON Parameters: id
	 *
	 * Activates the user and returns a the updated User object
	 */
	activateUser(userId: number): Observable<User> {
		return this.backend
			.post(ACTIVATE_USER_URL, ACTIVATE_USER_RESPONSE, { id: userId })
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Save User Basics
	 * =================
	 * API Endpoint: /web/hr/saveuserbasics
	 * JSON Parameters: UserBasics
	 *
	 * Saves the User Basics part of the form and returns an updated User object
	 */
	saveUserBasics(userBasics: UserBasics): Observable<User> {
		return this.backend
			.post(SAVE_USER_BASICS_URL, SAVE_USER_BASICS_RESPONSE, userBasics)
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Save User Appointments
	 * =======================
	 * API Endpoint: /web/hr/saveuserappointments
	 * JSON Parameters: id, appointments: Appointment[]
	 *
	 * Saves the User Appointment part of the form and returns the updated Appointment objects
	 */
	saveUserAppointments(
		userId: number,
		appointments: Appointment[]
	): Observable<Appointment[]> {
		return this.backend
			.post(SAVE_USER_APPOINTMENTS_URL, SAVE_USER_APPOINTMENTS_RESPONSE, {
				id: userId,
				appointments: appointments,
			})
			.pipe(
				map((response: AppointmentResponse) =>
					response.appointments.map((json) =>
						Appointment.fromJSON(json)
					)
				)
			);
	}

	/**
	 * Save User Qatar Info
	 * =====================
	 * API Endpoint: /web/hr/saveuserqatarinfo
	 * JSON Parameters: UserQatarInfo
	 *
	 * Saves the User Qatar part of the form
	 */
	saveUserQatarInfo(userQatarInfo: UserQatarInfo): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_USER_QATAR_INFO_URL,
			SAVE_USER_QATAR_INFO_RESPONSE,
			userQatarInfo
		);
	}

	/**
	 * Save User Insurance
	 * ====================
	 * API Endpoint: /web/hr/saveuserinsurance
	 * JSON Parameters: id, insurance: Insurance
	 *
	 * Saves the User Insurance part of the form
	 */
	saveUserInsurance(
		userId: number,
		insurance: Insurance
	): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_USER_INSURANCE_URL,
			SAVE_USER_INSURANCE_RESPONSE,
			{ id: userId, insurance: insurance }
		);
	}

	/**
	 * Save User Emergency Info
	 * =========================
	 * API Endpoint: /web/hr/saveuseremergencyinfo
	 * JSON Parameters: emergencyInfo: EmergencyInfo
	 *
	 * Saves the User Emergency Info part of the form and returns an updated user
	 */
	saveUserEmergencyInfo(emergencyInfo: EmergencyInfo): Observable<User> {
		return this.backend
			.post(
				SAVE_USER_EMERGENCY_INFO_URL,
				SAVE_USER_EMERGENCY_INFO_RESPONSE,
				emergencyInfo
			)
			.pipe(
				map((response: UserResponse) => User.fromJSON(response.user))
			);
	}

	/**
	 * Save User Dependents
	 * =========================
	 * API Endpoint: /web/hr/saveuserdependents
	 * JSON Parameters: id, dependents: Dependent[]
	 *
	 * Saves the User Dependents part of the form and returns an updated Dependents
	 */
	saveUserDependents(
		userId: number,
		dependents: Dependent[]
	): Observable<BaseResponse> {
		return this.backend.post(
			SAVE_USER_DEPENDENTS_URL,
			SAVE_USER_DEPENDENTS_RESPONSE,
			{
				id: userId,
				dependents: dependents,
			}
		);
	}
}
